import "swiper/css";

import classNames from "classnames";
import { Entry } from "contentful";
import { useTranslations } from "next-intl";
import { CSSProperties, useState } from "react";
import SwiperCore from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import Container from "~/components/common/container";
import Grid from "~/components/common/grid";
import Icon from "~/components/common/icon";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import { assertEditorialType } from "~/utils/editorial-utils";

import { updateArrowStatus } from "../carousel-utils/carousel-utils";
import styles from "./loyalty-table.module.scss";

type TableColumnProps = {
  column: string;
  columnSubtitle: string[];
  rowTitle: string[];
  indexColumn: number;
  type: "mobile" | "desktop";
};

export function TableColumn({ column, columnSubtitle, rowTitle, indexColumn, type }: TableColumnProps) {
  return (
    <>
      <div className={styles.columnHeader}>
        <div className={styles.columnTitle}>{column}</div>
        <div className={styles.columnSubtitle}>{columnSubtitle?.[indexColumn]}</div>
      </div>
      {rowTitle?.map((row, indexRow) => {
        return (
          <div key={`table-row-${type}-${row}-${indexRow}`} className={styles.row}>
            {Number(row.split("|")[indexColumn + 1]) === 1 ? (
              <div className={styles.iconContainer}>
                <Icon
                  name={type === "mobile" ? "checkmark-gradient" : "checkmark-gradient-v2"}
                  className={styles.icon}
                />
              </div>
            ) : (
              <div></div>
            )}
          </div>
        );
      })}
    </>
  );
}

type TableProps = {
  entry: Entry<Editorials.LoyaltyTable>;
};

export function LoyaltyTableMobile({ entry }: TableProps) {
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore>();
  const [arrowsStatus, setArrowsStatus] = useState({ arrowL: false, arrowR: true });
  const t = useTranslations();

  const { columnTitle, columnSubtitle, rowTitle } = entry.fields;
  const inspectorMode = contentfulUtils.useInspectorMode(entry);

  return (
    <Swiper
      onSwiper={setSwiperInstance}
      slidesPerView={1}
      spaceBetween={12}
      onTransitionEnd={(currentSwiper) => setArrowsStatus(updateArrowStatus(currentSwiper))}
      className={classNames(styles.slideColumnsContainer, styles.tableOnMobile)}
      speed={Constants.SWIPER_TRANSITION_DURATION}
    >
      {columnTitle?.map((column, indexColumn) => {
        return (
          <SwiperSlide
            key={`table-column-mobile-${column}-${indexColumn}`}
            className={classNames(styles.column, !swiperInstance ? styles.initialSlide : undefined)}
            style={{
              gridTemplateRows: `1.1fr repeat(${rowTitle?.length}, 1fr)`,
            }}
            {...inspectorMode?.getProps("columnTitle")}
          >
            <TableColumn
              column={column}
              columnSubtitle={columnSubtitle}
              rowTitle={rowTitle}
              indexColumn={indexColumn}
              type="mobile"
            />
          </SwiperSlide>
        );
      })}
      <button
        className={styles.swiperButtonPrev}
        disabled={!arrowsStatus.arrowL}
        onClick={() => swiperInstance?.slidePrev()}
        aria-label={t("a11y.previousSlide")}
      >
        <Icon name="chevron-left" />
      </button>
      <button
        className={styles.swiperButtonNext}
        disabled={!arrowsStatus.arrowR}
        onClick={() => swiperInstance?.slideNext()}
        aria-label={t("a11y.nextSlide")}
      >
        <Icon name="chevron-right" />
      </button>
    </Swiper>
  );
}

export function LoyaltyTableDesktop({ entry }: TableProps) {
  const { columnTitle, columnSubtitle, rowTitle } = entry.fields;
  const inspectorMode = contentfulUtils.useInspectorMode(entry);

  return (
    <div className={classNames(styles.slideColumnsContainer, styles.tableOnDesktop)}>
      {columnTitle?.map((column, indexColumn) => {
        return (
          <div
            key={`table-column-desktop-${column}-${indexColumn}`}
            className={styles.column}
            style={{
              gridTemplateRows: `1.2fr repeat(${rowTitle?.length}, 1fr)`,
            }}
            {...inspectorMode?.getProps("columnTitle")}
          >
            <TableColumn
              column={column}
              columnSubtitle={columnSubtitle}
              rowTitle={rowTitle}
              indexColumn={indexColumn}
              type="desktop"
            />
          </div>
        );
      })}
    </div>
  );
}

type Props = {
  entry: Entry<unknown>;
};

export default function LoyaltyTable(props: Props) {
  assertEditorialType<Editorials.LoyaltyTable>(props.entry, "loyaltyTable");

  const { columnTitle, rowTitle, backgroundColor, withoutContainer } = props.entry.fields;
  const inspectorMode = contentfulUtils.useInspectorMode(props.entry);

  const table = (
    <div className={styles.table} style={{ "--number-columns": columnTitle?.length + "fr" } as CSSProperties}>
      <div
        className={classNames(styles.column, styles.firstColumn)}
        style={{ "--number-rows": rowTitle?.length } as CSSProperties}
        {...inspectorMode?.getProps("rowTitle")}
      >
        <div></div>

        {rowTitle?.map((row, indexRow) => {
          return (
            <div key={`table-row-${row}-${indexRow}`} className={styles.rowFirstColumn}>
              {row.split("|")[0]}
            </div>
          );
        })}
      </div>

      <LoyaltyTableMobile entry={props.entry} />
      <LoyaltyTableDesktop entry={props.entry} />
    </div>
  );

  return (
    <div className={classNames(styles.background, backgroundColor ? styles[backgroundColor] : undefined)}>
      {withoutContainer ? (
        table
      ) : (
        <Container>
          <Grid>
            <div className={styles.container}>{table}</div>
          </Grid>
        </Container>
      )}
    </div>
  );
}
